import { RegistrationData } from '@sportninja/common/hooks/useRegistrationData'
import { Currency } from '@sportninja/common/types'
import { RegistrationTableDivision } from '../types'

/**
 * Helper function to get default currency object
 */
export const getDefaultCurrency = (): Currency => ({
  id: '',
  name: '',
  name_full: null,
  abbreviation: null,
})

/**
 * Optimized division computation with better performance characteristics
 * @param data - Raw registration data from the API
 * @returns Array of formatted division data
 */
export const computeDivisions = (
  data?: RegistrationData | null
): RegistrationTableDivision[] => {
  if (!data?.detailed?.divisions) {
    return []
  }

  // Cache frequently accessed values
  const detailedId = data.detailed.id
  const isSeasonType = data.detailed.type === 'Season'
  const defaultCurrency =
    data.detailed.registration?.currency || getDefaultCurrency()

  return data.detailed.divisions.map((division) => {
    const registeredTeams = division.registered_teams || []
    const registration = division.registration

    // Single pass through teams array for better performance
    let activeTeamCount = 0
    let bucketTeamCount = 0

    for (const team of registeredTeams) {
      if (team?.is_bucket) {
        bucketTeamCount++
      } else if (team.registration_status !== 5) {
        activeTeamCount++
      }
    }

    // Cache registration properties
    const divisionAge = registration?.division_age
    const divisionGender = registration?.division_gender
    const isSinglePayment =
      registration?.single_payment ||
      registration?.player_single_payment ||
      false

    return {
      isSeason: division.id === detailedId && isSeasonType,
      id: division.id,
      name: division.name,
      is_visible_standings: division.is_visible_standings,
      type: divisionAge?.name || '',
      open: registration?.is_registration_on === 1,
      gender: divisionGender?.name || '',
      genderId: divisionGender?.id || '',
      totalPlayers: registration?.total_players || 0,
      maxPlayers: registration?.max_players_per_team || 0,
      totalTeams: activeTeamCount,
      maxTeams: registration?.max_teams || 0,
      deposit: registration?.deposit || null,
      depositRequired: !!registration?.deposit_required,
      cost: registration?.team_cost || null,
      playerCost: registration?.player_cost || null,
      playerDeposit: registration?.player_deposit || null,
      playerDepositRequired: !!registration?.player_deposit_required,
      playerRequirePayment: !!registration?.player_require_payment,
      playerNumberOfInstallments:
        registration?.player_number_of_installments || null,
      playerInstallmentFrequency:
        registration?.player_installment_frequency || null,
      playerInstallmentStartDate:
        registration?.player_installment_start_date || null,
      revenue: registration?.total_paid || 0,
      teams: registeredTeams,
      registration: registration,
      currency: defaultCurrency,
      currency_code: data.detailed.registration?.currency_code,
      singlePayment: isSinglePayment,
      dayOfWeek: registration?.day_of_week || null,
      playerSinglePayment: registration?.player_single_payment || false,
      maxPlayersPerBucket: registration?.max_players_per_bucket || null,
      bucketTeams: bucketTeamCount,
    }
  })
}
